import { findStructInfo, findEnumInfo, typeRefToString } from '$lib/stores/schema.svelte';
import type { WandPortDesc } from '$lib/types';

const PRIMITIVE_DESCRIPTIONS: Record<string, string> = {
  bool: 'A boolean value (true or false)',
  byte: 'An 8-bit integer (alias for i8)',
  i8: 'An 8-bit integer',
  i16: 'A 16-bit integer',
  i32: 'A 32-bit integer',
  i64: 'A 64-bit integer',
  double: 'A double-precision floating-point number',
  string: 'A single string value',
  binary: 'An opaque byte string',
  python_code: 'Executable Python source code',
};

/**
 * Determine the fence language tag for a given type.
 * - Code → python (executable code)
 * - string → text (plain text)
 * - structs, enums, numeric/bool primitives → json (structured data)
 */
export function fenceLangForType(typeName: string): string {
  if (typeName === 'Code' || typeName === 'Python') return 'python';
  if (typeName === 'string') return 'text';
  return 'json';
}

type DescriptorFn = (typeName: string) => string | null;

const customDescriptors: Map<string, DescriptorFn> = new Map();

export function registerDescriptor(typeName: string, fn: DescriptorFn) {
  customDescriptors.set(typeName, fn);
}

function autoDescribe(typeName: string): string | null {
  if (typeName in PRIMITIVE_DESCRIPTIONS) {
    return PRIMITIVE_DESCRIPTIONS[typeName];
  }

  const structInfo = findStructInfo(typeName);
  if (structInfo) {
    if (structInfo.doc) return structInfo.doc;
    const fields = structInfo.fields
      .map((f) => `  - "${f.name}" (${typeRefToString(f.type)})`)
      .join('\n');
    return `A "${typeName}" object with fields:\n${fields}`;
  }

  const enumInfo = findEnumInfo(typeName);
  if (enumInfo) {
    if (enumInfo.doc) return enumInfo.doc;
    const values = enumInfo.values.map((v) => v.name).join(', ');
    return `One of: ${values}`;
  }

  return null;
}

export function describeType(typeName: string): string {
  const custom = customDescriptors.get(typeName);
  if (custom) {
    const result = custom(typeName);
    if (result) return result;
  }
  const auto = autoDescribe(typeName);
  if (auto) return auto;
  throw new Error(`Unknown type "${typeName}" - not a primitive, struct, enum, or custom descriptor`);
}

/**
 * Build "## Expected output" section for ALL output ports.
 */
export function buildOutputFormatPrompt(outputPorts: WandPortDesc[]): string {
  if (outputPorts.length === 0) return '';

  const parts: string[] = [];
  for (const port of outputPorts) {
    const rendered = typeRefToString(port.type);
    parts.push(`- **${port.name}** (${rendered}): ${describeType(rendered)}`);
  }

  return '## Expected output\n' + parts.join('\n');
}
